/**
 * Convert a self-contained HTML document into a PDF and trigger a
 * direct download — bypassing the browser's print dialog entirely so
 * there are never any "about:blank" / URL / timestamp headers or
 * footers stamped onto the file. The HTML is rendered in a hidden
 * iframe (so its inline styles, fonts, and images resolve naturally)
 * and `html2pdf.js` is dynamic-imported on demand so it doesn't bloat
 * the initial bundle.
 */
export async function downloadPdf(html: string, filename: string) {
  // Render the HTML in an off-screen iframe. The iframe gives the
  // document its own scope (so the parent app's CSS doesn't leak in),
  // and lets the browser parse <head>, <style>, and inline styles
  // exactly as if it were a standalone page.
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.left = '-10000px'
  iframe.style.top = '0'
  // Width tuned to ~A4 at 96 DPI so the rendered layout doesn't get
  // squeezed to a phone-narrow viewport before html2canvas snapshots it.
  iframe.style.width = '794px'
  iframe.style.height = '0'
  iframe.style.border = '0'
  document.body.appendChild(iframe)

  try {
    await new Promise<void>((resolve, reject) => {
      iframe.addEventListener('load', () => resolve(), { once: true })
      iframe.addEventListener('error', () => reject(new Error('iframe load failed')), { once: true })
      iframe.srcdoc = html
    })

    const doc = iframe.contentDocument
    if (!doc) throw new Error('iframe document not available')

    // Wait for any embedded images (logos, QR, charts) to finish so
    // html2canvas doesn't snapshot a half-loaded page.
    await Promise.all(Array.from(doc.images).map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((r) => {
            img.addEventListener('load', () => r(), { once: true })
            img.addEventListener('error', () => r(), { once: true })
          })
    ))

    // Resize the iframe to its content's full height before snapshotting
    // so the entire document is rasterised, not just the visible bit.
    const fullHeight = doc.documentElement.scrollHeight
    iframe.style.height = `${fullHeight}px`

    // Dynamic-import keeps html2pdf.js (~150 KB minified) out of the
    // initial bundle — only paid for when the user actually exports.
    const mod = await import('html2pdf.js') as { default: any }
    const html2pdf = mod.default

    await html2pdf()
      .set({
        margin: 10,
        filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          windowWidth: 794,
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      })
      .from(doc.body)
      .save()
  } finally {
    iframe.remove()
  }
}
